import { useCallback, useMemo } from "react";
import { SelectionType } from "src/js/stores/map/store";
import { DesignationDistance } from "react-migration/domains/constraints/designation/hooks/useNearestDesignationQuery";
import { DesignationHatch } from "react-migration/domains/constraints/components/Hatch";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { UserUnitLength } from "react-migration/components/UserUnitLength";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useWorkbench } from "react-migration/layouts/map/Multilayer/Bundle/Workbench/WorkbenchContext";
import { isDefined } from "react-migration/lib/util/isDefined";
import { Icon } from "react-migration/components/Icon";

import { MAX_AMENITY_RADIUS_METERS } from "./constants";

type AmenityDistanceInsightProps = {
  subCategoryId: string;
  designationDistance?: DesignationDistance;
  loading?: boolean;
};
export function AmenityDistanceInsight({ subCategoryId, designationDistance, loading }: AmenityDistanceInsightProps) {
  const { t } = useTranslation();
  const { setDetailSelection } = useWorkbench();

  const onSelectAmenity = useCallback(
    () =>
      designationDistance &&
      setDetailSelection({
        type: SelectionType.DESIGNATION,
        id: designationDistance.designation.id,
      }),
    [designationDistance, setDetailSelection]
  );

  const hatchDesignation = useMemo(() => {
    // prefer real designation so that attributes can be used for styling
    if (designationDistance) return designationDistance.designation;

    return {
      sub_category_id: subCategoryId,
      designation_attributes: [],
    };
  }, [designationDistance, subCategoryId]);

  if (loading) {
    return (
      <div className="relative p-2 h-[38px]">
        <SkeletonLoading cols={1} rows={1} showPadding={false} bgClassName="bg-transparent" />
      </div>
    );
  }

  const { designation, distanceMeters } = designationDistance ?? {};
  const beyondMaxDistance = isBeyondMaxDistance(distanceMeters, MAX_AMENITY_RADIUS_METERS);
  const distanceMetersDisplay = beyondMaxDistance ? MAX_AMENITY_RADIUS_METERS : distanceMeters;
  const onSite = !beyondMaxDistance && distanceMeters === 0;

  return (
    <div className="flex items-center gap-3" data-testid={`distance-insight-${subCategoryId}`}>
      <div className="shrink-0">
        <DesignationHatch designation={hatchDesignation} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-normal truncate cursor-default text-text-base-primary">
          {t(`workbench.summary.amenities.display.${subCategoryId}`)}
        </div>
        {designation?.id ? (
          <span
            className="max-w-full inline-flex items-center gap-x-0.5 text-text-base-active hover:text-text-base-active-hover leading-none select-none cursor-pointer"
            onClick={onSelectAmenity}
          >
            <span className="truncate" title={designation.display_name} data-testid="distance-insight__name">
              {designation?.display_name}
            </span>
            <Icon icon="icon-lt-arrow-right-line" size="sm" cursor="pointer" />
          </span>
        ) : (
          <div
            className="text-xs truncate cursor-default text-text-base-secondary-a"
            data-testid="distance-insight__name"
          >
            None nearby
          </div>
        )}
      </div>

      <div className="text-right shrink-0" data-testid="distance-insight__distance">
        {onSite ? (
          <span className="text-sm font-medium text-text-base-secondary">On site</span>
        ) : (
          <UserUnitLength lengthMeters={distanceMetersDisplay} prefix={beyondMaxDistance ? ">" : ""} />
        )}
      </div>
    </div>
  );
}

export function isBeyondMaxDistance(distanceMeters: number | undefined, maxDistanceMeters: number) {
  return !isDefined(distanceMeters) || !isFinite(distanceMeters as number) || distanceMeters > maxDistanceMeters;
}
